/**
 * NOTE: This file is part of the legacy Notes feature.
 * User-facing text has been renamed to "Documentation" for clarity.
 * For the new Notes feature, check the Hub implementation.
 */

import { Note } from "@/lib/api/notes.ts";
import { Session } from "@/lib/api/sessions";
import { Fragment, ReactNode, useMemo } from "react";
import { AddSession } from "@/components/session/add-session.tsx";

interface UpcomingNotesWithSessionsProps {
  menteeId: string;
  mentorId: string;

  notes: Note[];
  sessions: Session[];

  renderNote: (note: Note) => ReactNode;
  renderSession: (session: Session) => ReactNode;
  readonlyMode?: boolean;
  emptyMessage?: string;
}

interface NoteWithSession extends Note {
  type: "note";
}

interface SessionWithNote extends Session {
  type: "session";
}

type NoteOrSession = NoteWithSession | SessionWithNote;

export function UpcomingNotesWithSession({
  notes,
  sessions,
  renderSession,
  renderNote,
  emptyMessage,
  menteeId,
  readonlyMode,
}: UpcomingNotesWithSessionsProps) {
  const list = useMemo(() => {
    const listArr: NoteOrSession[] = [];

    notes.forEach((note) => {
      listArr.push({
        ...note,
        type: "note",
      });
    });

    sessions.forEach((session) => {
      listArr.push({
        ...session,
        type: "session",
      });
    });

    return listArr.toSorted((a, b) => {
      const aDate = a.type === "note" ? a.reminder! : a.scheduled_time;
      const bDate = b.type === "note" ? b.reminder! : b.scheduled_time;

      return Date.parse(aDate) - Date.parse(bDate);
    });
  }, [sessions, notes]);

  return (
    <div className="flex h-full w-full flex-col pb-20">
      <div className="flex-1 overflow-y-auto pb-4">
        {list.length > 0 ? (
          list.map((item) => {
            if (item.type === "note") {
              return <Fragment key={item.id}>{renderNote(item)}</Fragment>;
            } else {
              return <Fragment key={item.id}>{renderSession(item)}</Fragment>;
            }
          })
        ) : (
          <div className="flex h-full items-center justify-center text-gray-500">
            {emptyMessage || "No upcoming documentation or sessions"}
          </div>
        )}
      </div>
      {!readonlyMode && <AddSession menteeId={menteeId} />}
    </div>
  );
}
